// Step 7: Review & Save - summary of all entered data
// Shows completion status per section with publish-readiness progress bar
// Highlights missing required fields for published tours with clickable step links
// Updated: Added progress indicator, better visual distinction for errors vs complete

import React from 'react';
import { AlertTriangle, Clock, MapPin, DollarSign, FileText, Package, Image, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { Tour } from '../../../types/tours';
import {
  validateBasics,
  validateSchedule,
  validatePricing,
  validateDescriptions,
  validateItems,
  validateImages,
} from '../../../utils/tourValidation';

interface StepReviewProps {
  formData: Partial<Tour>;
  isConcept: boolean;
  onGoToStep: (step: number) => void;
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  isComplete: boolean;
  errors: string[];
  stepIndex: number;
  onGoToStep: (step: number) => void;
  children: React.ReactNode;
}

const ReviewSection: React.FC<SectionProps> = ({ title, icon, isComplete, errors, stepIndex, onGoToStep, children }) => (
  <div className={`rounded-lg p-5 border transition-colors ${
    errors.length > 0
      ? 'bg-red-500/5 border-red-500/20'
      : isComplete
        ? 'bg-emerald-500/5 border-emerald-500/20'
        : 'bg-white/5 border-white/10'
  }`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className={`p-1.5 rounded-lg ${
          errors.length > 0
            ? 'bg-red-500/15 text-red-400'
            : isComplete
              ? 'bg-emerald-500/15 text-emerald-400'
              : 'bg-white/10 text-white/40'
        }`}>
          {icon}
        </div>
        <h4 className="font-medium text-white">{title}</h4>
        {isComplete && errors.length === 0 ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        ) : errors.length > 0 ? (
          <div className="flex items-center gap-1.5">
            <XCircle className="w-4 h-4 text-red-400" />
            <span className="text-xs text-red-400 font-medium">
              {errors.length} {errors.length === 1 ? 'issue' : 'issues'}
            </span>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onGoToStep(stepIndex)}
        className="flex items-center gap-1 text-xs text-[#E85C0D] hover:text-[#ff6a0d] transition-colors font-medium"
      >
        {errors.length > 0 ? 'Fix' : 'Edit'}
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
    <div className="text-sm text-white/60 space-y-1 ml-10">
      {children}
    </div>
    {errors.length > 0 && (
      <div className="mt-3 ml-10 space-y-1.5">
        {errors.map((err, i) => (
          <p key={i} className="text-xs text-red-400/90 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-red-400/60 shrink-0" />
            {err}
          </p>
        ))}
      </div>
    )}
  </div>
);

const StepReview: React.FC<StepReviewProps> = ({ formData, isConcept, onGoToStep }) => {
  const isPublished = !isConcept;
  const basics = validateBasics(formData);
  const schedule = validateSchedule(formData, isPublished);
  const pricing = validatePricing(formData, isPublished);
  const descriptions = validateDescriptions(formData, isPublished);
  const items = validateItems(formData);
  const images = validateImages(formData, isPublished);

  const requiredSections = [basics, schedule, pricing, descriptions, images];
  const completedRequired = requiredSections.filter(s => s.isComplete && s.errors.length === 0).length;
  const totalRequired = requiredSections.length;
  const totalErrors = requiredSections.reduce((sum, s) => sum + s.errors.length, 0);
  const progressPercent = isPublished ? Math.round((completedRequired / totalRequired) * 100) : 100;

  const priceDisplay = [];
  if (formData.group_tour_price) priceDisplay.push(`Group: $${formData.group_tour_price}`);
  if (formData.reduced_group_price) priceDisplay.push(`Reduced: $${formData.reduced_group_price}`);
  if (formData.private_price) priceDisplay.push(`Private: $${formData.private_price}`);

  return (
    <div className="space-y-4">
      <div className={`p-5 rounded-xl border ${
        isConcept
          ? 'bg-amber-500/10 border-amber-500/30'
          : totalErrors === 0
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : 'bg-red-500/10 border-red-500/30'
      }`}>
        <div className="flex items-start gap-3">
          {isConcept ? (
            <div className="p-2 rounded-full bg-amber-500/20 shrink-0 mt-0.5">
              <Clock className="w-4 h-4 text-amber-400" />
            </div>
          ) : totalErrors === 0 ? (
            <div className="p-2 rounded-full bg-emerald-500/20 shrink-0 mt-0.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
          ) : (
            <div className="p-2 rounded-full bg-red-500/20 shrink-0 mt-0.5">
              <AlertTriangle className="w-4 h-4 text-red-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${
              isConcept
                ? 'text-amber-400'
                : totalErrors === 0 ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {isConcept
                ? 'This tour will be saved as a concept and will not be visible to the public.'
                : totalErrors === 0
                  ? 'Ready to publish. All required fields are complete.'
                  : `Not ready to publish. ${totalErrors} issue${totalErrors > 1 ? 's' : ''} remaining.`}
            </p>
            {isPublished && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-white/40">Publish readiness</span>
                  <span className={`font-medium ${
                    progressPercent === 100 ? 'text-emerald-400' : 'text-white/60'
                  }`}>
                    {completedRequired}/{totalRequired} sections complete
                  </span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      progressPercent === 100 ? 'bg-emerald-500' : progressPercent > 50 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ReviewSection
        title="Basics"
        icon={<FileText className="w-4 h-4" />}
        isComplete={basics.isComplete}
        errors={basics.errors}
        stepIndex={0}
        onGoToStep={onGoToStep}
      >
        <p>{formData.name || 'No name set'}</p>
      </ReviewSection>

      <ReviewSection
        title="Schedule & Logistics"
        icon={<Clock className="w-4 h-4" />}
        isComplete={schedule.isComplete}
        errors={schedule.errors}
        stepIndex={1}
        onGoToStep={onGoToStep}
      >
        {formData.location && <p>Location: {formData.location}</p>}
        {formData.departure && <p>Departure: {formData.departure}</p>}
        {formData.duration && <p>Duration: {formData.duration}</p>}
        {formData.activity_level && <p>Activity: {formData.activity_level}</p>}
        {formData.meeting_point && <p>Meeting: {formData.meeting_point}</p>}
        {!formData.location && !formData.departure && !formData.duration && (
          <p className="text-white/30 italic">No schedule information added yet</p>
        )}
      </ReviewSection>

      <ReviewSection
        title="Pricing"
        icon={<DollarSign className="w-4 h-4" />}
        isComplete={pricing.isComplete}
        errors={pricing.errors}
        stepIndex={2}
        onGoToStep={onGoToStep}
      >
        {priceDisplay.length > 0 ? (
          priceDisplay.map((p, i) => <p key={i}>{p}</p>)
        ) : (
          <p className="text-white/30 italic">No pricing added yet</p>
        )}
      </ReviewSection>

      <ReviewSection
        title="Descriptions"
        icon={<MapPin className="w-4 h-4" />}
        isComplete={descriptions.isComplete}
        errors={descriptions.errors}
        stepIndex={3}
        onGoToStep={onGoToStep}
      >
        {formData.tour_detail_headline && <p>Headline: {formData.tour_detail_headline}</p>}
        {formData.tour_card_description && <p>Card: {formData.tour_card_description}</p>}
        {formData.availability && <p>Availability: {formData.availability}</p>}
        {!formData.tour_detail_headline && !formData.tour_card_description && (
          <p className="text-white/30 italic">No descriptions added yet</p>
        )}
      </ReviewSection>

      <ReviewSection
        title="Items"
        icon={<Package className="w-4 h-4" />}
        isComplete={items.isComplete}
        errors={items.errors}
        stepIndex={4}
        onGoToStep={onGoToStep}
      >
        <p>{formData.include_items?.length || 0} included items, {formData.bring_items?.length || 0} bring items</p>
      </ReviewSection>

      <ReviewSection
        title="Images"
        icon={<Image className="w-4 h-4" />}
        isComplete={images.isComplete}
        errors={images.errors}
        stepIndex={5}
        onGoToStep={onGoToStep}
      >
        <p>{formData.carousel_images?.length || 0} carousel, {formData.gallery_images?.length || 0} gallery images</p>
      </ReviewSection>
    </div>
  );
};

export default StepReview;
